import React, { ReactNode, useState } from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import { useSelector } from "react-redux";
import { LanguageNameSpaces, TimeRange } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { BASE_CHART_TIME_RANGES } from "@shared/common/constants";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { DISPLAY_CURRENCIES, TIME_RANGE_1_DAY } from "common/constants";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import ChartEmpty from "common/NewLook/ChartEmpty.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import BalanceChart, {
  BalancePoint,
} from "features/dashboard/instrument/BalanceChart.tsx";
import {
  formatCompactMoney,
  formatMoney,
  formatSignedMoney,
  formatSignedPercent,
  splitFraction,
} from "features/portfolio/NewLook/format.ts";
import { formatDateLabel } from "@shared/utils/date";

interface NewLookBalanceHistoryProps {
  points: BalancePoint[];
  timeRange: TimeRange;
  onTimeRangeChange: (timeRange: TimeRange) => void;
  loading: boolean;
  title: ReactNode;
  /**
   * The balance now. Given, it leads (display figure, following the
   * crosshair) with the change under it; without it the change leads.
   */
  balance?: number;
  chartHeight: number;
  "data-testid"?: string;
}

/**
 * A balance over a selected range, the Dashboard's way: a headline that
 * follows the crosshair, a smooth line with dotted guides, and the ranges as
 * a segmented control.
 */
const NewLookBalanceHistory = ({
  points,
  timeRange,
  onTimeRangeChange,
  loading,
  title,
  balance,
  chartHeight,
  "data-testid": testId,
}: NewLookBalanceHistoryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const tokens = getNewLookTokens(useTheme());
  const displayCurrency = useSelector(selectDisplayCurrency);
  const symbol = displayCurrency === DISPLAY_CURRENCIES.BTC ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const intraday = timeRange === TIME_RANGE_1_DAY;
  const [hovered, setHovered] = useState<BalancePoint>();

  const first = points[0]?.value;
  const last = hovered?.value ?? points[points.length - 1]?.value;
  const change = first !== undefined && last !== undefined ? last - first : 0;
  const changePercent = first ? (change / first) * 100 : 0;
  const color = directionColor(tokens, change);
  const pending = loading && !points.length;
  const hoveredTime = hovered
    ? formatDateLabel(moment.unix(hovered.time), intraday ? "HH:mm" : "MMM D")
    : undefined;
  const rangeMeta = t("overview.over_range", {
    range: timeRange.toUpperCase(),
  });

  const tabs = (
    <SegmentTabs
      options={BASE_CHART_TIME_RANGES as TimeRange[]}
      value={timeRange}
      onChange={onTimeRangeChange}
      ariaLabel={t("overview.range_label")}
    />
  );

  const renderChart = () => {
    if (pending)
      return <Skeleton width="100%" height={chartHeight} radius={8} />;
    if (points.length < 2)
      return (
        <ChartEmpty height={chartHeight}>{t("overview.no_history")}</ChartEmpty>
      );
    return (
      <BalanceChart
        points={points}
        height={chartHeight}
        intraday={intraday}
        hourly={true}
        onActiveChange={setHovered}
        formatValue={(value) => formatMoney(value, symbol, precision)}
        formatAxisValue={(value, step) =>
          formatCompactMoney(value, symbol, step)
        }
        ariaLabel={t("overview.chart_label", {
          range: timeRange.toUpperCase(),
        })}
      />
    );
  };

  const changeFigures = (
    <>
      <ChangeValue style={{ color }}>
        {formatSignedMoney(change, symbol, precision)}
      </ChangeValue>
      <ChangeValue style={{ color }}>
        {formatSignedPercent(changePercent)}
      </ChangeValue>
    </>
  );

  if (balance !== undefined) {
    const headline = hovered?.value ?? balance;
    const [whole, fraction] = splitFraction(
      getLocaleStringWithPrecision(headline, precision)
    );
    return (
      <Wrapper data-testid={testId}>
        <Header $align="flex-start">
          <div>
            <Label>{title}</Label>
            {pending ? (
              <Skeleton width={200} height={36} style={{ marginTop: 6 }} />
            ) : (
              <Display>
                <Currency>{symbol}</Currency>
                {whole}
                {fraction && <Fraction>{fraction}</Fraction>}
              </Display>
            )}
            <ChangeLine aria-live="polite">
              {!pending && changeFigures}
              <Meta>
                {hoveredTime
                  ? t("overview.since", {
                      time: formatDateLabel(
                        moment.unix(points[0].time),
                        intraday ? "HH:mm" : "MMM D"
                      ),
                    })
                  : rangeMeta}
              </Meta>
            </ChangeLine>
          </div>
          {tabs}
        </Header>
        <ChartArea $gap={16}>{renderChart()}</ChartArea>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid={testId}>
      <Header $align="center">
        <Title>{title}</Title>
        {tabs}
      </Header>
      <Figures aria-live="polite">
        {pending ? (
          <Skeleton width={180} height={20} />
        ) : (
          <>
            <ChangeLarge style={{ color }}>
              {formatSignedMoney(change, symbol, precision)}
            </ChangeLarge>
            <ChangeValue style={{ color }}>
              {formatSignedPercent(changePercent)}
            </ChangeValue>
            <Meta>
              {hovered
                ? `${formatMoney(hovered.value, symbol, precision)} · ${
                    hoveredTime ?? ""
                  }`
                : rangeMeta}
            </Meta>
          </>
        )}
      </Figures>
      <ChartArea $gap={12}>{renderChart()}</ChartArea>
    </Wrapper>
  );
};

export default React.memo(NewLookBalanceHistory);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const Header = styled.div<{ $align: string }>`
  display: flex;
  align-items: ${(p) => p.$align};
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  min-height: 30px;
`;
const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Display = styled.div`
  margin-top: 4px;
  ${newLookType.display};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Currency = styled.span`
  margin-right: 1px;
  font-size: 0.72em;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Fraction = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChangeLine = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 6px;
`;
const Figures = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-height: 26px;
  margin-top: 6px;
`;
const ChangeLarge = styled.span`
  ${newLookType.figureLarge};
`;
const ChangeValue = styled.span`
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChartArea = styled.div<{ $gap: number }>`
  margin-top: ${(p) => p.$gap}px;
`;
